import React from 'react';
import { theme } from '../../styles/theme';
import type { Item } from '../../types/game';
import { hasPassive } from '../../data/passives';
import type { RunState } from '../../types/run';

/**
 * Kill round summary — shown between the GameScreen "Continue" click
 * and the passive-draft-round pick. Gives the player a clear itemized
 * breakdown of what they earned this round (kill bounty + items) before
 * the next decision is asked of them.
 *
 * Mirrors the extract-round summary that lives in CardDropScreen, but
 * dedicated so the kill flow doesn't get interleaved with the "drop a
 * card" UI.
 */
interface KillSummaryScreenProps {
  runState: RunState;
  killMarks: number;         // bounty from the kill itself (tuning.marks.killReward)
  passphraseMarks: number;   // marks from collected passphrases this round
  collectedItems: Item[];    // items the player was carrying on kill
  onContinue: () => void;
  round: number;
}

export function KillSummaryScreen({ runState, killMarks, passphraseMarks, collectedItems, onContinue, round }: KillSummaryScreenProps) {
  // Items sold on kill use the same Bank Job passive multiplier as extract.
  // Items ARE banked on kill (unlike extract which banks marks), because
  // killing the agent makes the run safe — there's no "you forfeit items
  // on death" risk once the kill lands.
  const bankJobMultiplier = hasPassive(runState, 'bankJob') ? 1.5 : 1;
  const itemsTotal = Math.floor(
    collectedItems.reduce((sum, item) => sum + (item.sellValue ?? 10), 0) * bankJobMultiplier,
  );
  const total = killMarks + passphraseMarks + itemsTotal;

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.resultLabel}>TARGET NEUTRALIZED</div>
        <div style={styles.title}>ROUND {round} SUMMARY</div>
        <div style={styles.subtitle}>
          Mission complete. Payout accounted for — you'll pick a passive next.
        </div>

        {/* Itemized breakdown */}
        <div style={styles.breakdownCard}>
          <div style={styles.breakdownRow}>
            <span style={styles.breakdownLabel}>Kill bounty</span>
            <span style={{ ...styles.breakdownValue, color: '#44ff88' }}>+{killMarks}</span>
          </div>

          {passphraseMarks > 0 && (
            <div style={styles.breakdownRow}>
              <span style={styles.breakdownLabel}>Passphrases</span>
              <span style={{ ...styles.breakdownValue, color: '#44aaff' }}>+{passphraseMarks}</span>
            </div>
          )}

          {collectedItems.length > 0 && (
            <>
              <div style={{ ...styles.breakdownRow, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px', marginTop: '8px' }}>
                <span style={{ ...styles.breakdownLabel, fontWeight: 700, color: theme.colors.textPrimary }}>
                  Items carried{bankJobMultiplier > 1 ? ' (Bank Job ×1.5)' : ''}
                </span>
                <span style={{ ...styles.breakdownValue, color: '#ffcc44' }}>+{itemsTotal}</span>
              </div>
              {collectedItems.map((item, i) => {
                const sell = Math.floor((item.sellValue ?? 10) * bankJobMultiplier);
                return (
                  <div key={`${item.id}-${i}`} style={{ ...styles.breakdownRow, fontSize: '11px', padding: '2px 0', color: theme.colors.textSecondary }}>
                    <span style={{ paddingLeft: '16px' }}>· {item.name}</span>
                    <span style={{ color: theme.colors.textDim }}>+{sell}</span>
                  </div>
                );
              })}
            </>
          )}

          {/* Total */}
          <div style={{
            ...styles.breakdownRow,
            borderTop: '1px solid rgba(68, 204, 136, 0.35)',
            paddingTop: '12px',
            marginTop: '12px',
          }}>
            <span style={{ ...styles.breakdownLabel, fontWeight: 700, color: theme.colors.textPrimary, letterSpacing: '2px' }}>
              TOTAL THIS ROUND
            </span>
            <span style={{ ...styles.breakdownValue, color: '#44ff88', fontSize: '22px' }}>+{total}</span>
          </div>
        </div>

        <div style={styles.runningTotal}>
          <span style={styles.runningLabel}>Banked marks</span>
          <span style={styles.runningValue}>{runState.marks + runState.unbankedMarks}</span>
        </div>

        <button style={styles.continueButton} onClick={onContinue}>
          CONTINUE →
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed', inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: theme.fonts.mono, overflow: 'auto',
    zIndex: 100,
  },
  inner: {
    width: '100%', maxWidth: '540px', padding: '40px 32px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  resultLabel: {
    fontSize: '12px',
    letterSpacing: '4px',
    fontWeight: 700,
    color: '#44ff44',
    marginBottom: '24px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    letterSpacing: '4px',
    color: theme.colors.textPrimary,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    letterSpacing: '0.5px',
    marginBottom: '32px',
    textAlign: 'center',
    lineHeight: '1.6',
  },
  breakdownCard: {
    width: '100%',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(68, 204, 136, 0.25)',
    borderRadius: '8px',
    padding: '18px 22px',
    marginBottom: '20px',
  },
  breakdownRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    padding: '5px 0',
    fontSize: '13px',
  },
  breakdownLabel: { color: theme.colors.textSecondary, letterSpacing: '0.5px' },
  breakdownValue: { fontWeight: 700, fontFamily: theme.fonts.mono },
  runningTotal: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    width: '100%', padding: '10px 22px', marginBottom: '28px',
    background: 'rgba(68, 204, 136, 0.05)',
    border: '1px solid rgba(68, 204, 136, 0.15)',
    borderRadius: '4px',
    fontSize: '11px',
  },
  runningLabel: { color: theme.colors.textDim, letterSpacing: '2px' },
  runningValue: { color: '#44cc88', fontWeight: 700, fontSize: '18px' },
  continueButton: {
    background: 'linear-gradient(180deg, rgba(68, 204, 136, 0.25), rgba(68, 204, 136, 0.1))',
    border: '1px solid rgba(68, 204, 136, 0.6)',
    color: '#88ddaa',
    padding: '14px 36px',
    fontFamily: theme.fonts.mono,
    fontSize: '13px', letterSpacing: '3px', fontWeight: 700,
    borderRadius: '4px', cursor: 'pointer',
    transition: 'all 0.15s',
  },
};
